import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Static, single-product upsell card in the cart drawer ("D'autres clients
 * ont également acheté :"). Clicking "Ajouter" adds the (hardcoded) product
 * to the cart for real, then hides this card.
 *
 * Reuses the same reasoning already proven in cart-line-color-swap.js:
 * - /cart/add.js with an {items: [...], sections: ...} body returns only
 *   {items, sections} - missing token/total_price/currency/item_count that
 *   CartLinesUpdateEvent.createCartFromAjaxResponse expects, and it throws
 *   (confirmed by running Shopify's own real code against a real response,
 *   see cart-line-color-swap.js's class doc for the full trace). Resolved
 *   via #resolveWithFullCart() - an extra /cart.json fetch, mirroring
 *   assets/product-form.js's native #refreshCart() - instead of converting
 *   the lean add.js response directly, so the event's promise carries real
 *   cart data instead of always resolving with cart:null (found while
 *   investigating a report of Shopify's own analytics/conversion funnel not
 *   registering cart activity added this way).
 * - Dispatches the standard CartLinesUpdateEvent so cart-items-component's
 *   existing global listener refreshes every cart surface - no bespoke
 *   morphing code here either.
 *
 * New-line positioning: Shopify has no "add at position" endpoint - a newly
 * added line always lands at the end of the cart. Since the ask is
 * specifically for the new line to appear at the TOP, this waits for that
 * row to actually show up after the refresh (a MutationObserver, not a
 * guessed delay - the exact kind of timing assumption that has already
 * caused real bugs elsewhere in this cart) and moves it there with a plain
 * DOM prepend(). Purely visual/client-side; the cart's real line order on
 * Shopify's side is unaffected (harmless - nothing reads that order back).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} status - Hidden live region for success/error messaging.
 *
 * @extends {Component<Refs>}
 */
export class CartUpsellComponent extends Component {
  /** @param {PointerEvent} event */
  addToCart(event) {
    event.preventDefault();

    if (this.hasAttribute('data-loading')) return;

    const variantId = Number(this.dataset.variantId);
    if (!variantId) return;

    this.setAttribute('data-loading', '');
    this.#setStatus('');

    const addPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId.toString(), quantity: 1 }],
        promise: addPromise.promise,
      })
    );

    fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', {
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: this.#cartSectionIds().join(','),
        }),
      })
    )
      .then((response) => response.json())
      .then((response) => {
        // Shopify's /cart/add.js returns a truthy "status" (e.g. 422) plus a
        // "message" on failure.
        if (response.status) {
          addPromise.reject(new Error(response.message || 'Add to cart failed'));
          this.#setStatus(response.message || 'Impossible d’ajouter ce produit au panier.');
          this.removeAttribute('data-loading');
          return;
        }

        this.#resolveWithFullCart(addPromise, response);

        const newLineKey = response.items?.[0]?.key;

        // The add already succeeded - hide immediately rather than waiting
        // for the refresh round-trip. The server-rendered cart no longer
        // includes this card either way (the Liquid condition that renders
        // it explicitly excludes products already in the cart), so this
        // isn't fighting the eventual morph, just not waiting on it.
        this.remove();

        if (newLineKey) this.#moveLineToTop(newLineKey);
      })
      .catch((error) => {
        console.error('[cart-upsell]', error);
        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error while adding recommended product',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
        this.#setStatus('Une erreur réseau est survenue, merci de réessayer.');
        this.removeAttribute('data-loading');
      });
  }

  /**
   * Resolves the CartLinesUpdateEvent's promise with cart data built from a
   * FULL /cart.json fetch, not the leaner add.js response - see class doc.
   * Mirrors assets/product-form.js's #refreshCart(). Not awaited by the
   * caller: this.remove() and #moveLineToTop() must run right away
   * regardless of how long this extra fetch takes.
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} promiseHandle
   * @param {{sections?: unknown, items?: unknown}} addResponse
   */
  async #resolveWithFullCart(promiseHandle, addResponse) {
    let cart = null;
    try {
      const fullCart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((response) => response.json());
      cart = CartLinesUpdateEvent.createCartFromAjaxResponse(fullCart);
    } catch (error) {
      console.warn('[cart-upsell] full cart fetch/conversion failed, continuing anyway', error);
    }

    promiseHandle.resolve({
      cart,
      detail: {
        sections: addResponse.sections,
        items: addResponse.items,
        source: 'cart-upsell',
        didError: false,
      },
    });
  }

  /**
   * Waits for the newly-added line's row to appear in the DOM, then moves it
   * to the top of its row list. Robust to however long the cart refresh
   * actually takes, rather than a fixed delay.
   * @param {string} lineKey
   */
  #moveLineToTop(lineKey) {
    const selector = `[data-key="${CSS.escape(lineKey)}"]`;

    const existing = document.querySelector(selector);
    if (existing) {
      this.#prependRow(existing);
      return;
    }

    const observer = new MutationObserver(() => {
      const row = document.querySelector(selector);
      if (row) {
        observer.disconnect();
        this.#prependRow(row);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Safety net - don't keep observing indefinitely if the row never shows
    // up (e.g. an unrelated error interrupted the refresh).
    setTimeout(() => observer.disconnect(), 5000);
  }

  /** @param {Element} row */
  #prependRow(row) {
    const parent = row.parentElement;
    if (parent && parent.firstElementChild !== row) {
      parent.prepend(row);
    }
  }

  /** @returns {string[]} Section IDs of every cart-items-component on the page. */
  #cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(/** @returns {id is string} */ (id) => Boolean(id));
  }

  /** @param {string} message */
  #setStatus(message) {
    const { status } = this.refs;
    if (status) status.textContent = message;
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
